import { Helmet } from 'react-helmet-async'
import { useLocation, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'

import PendingApprovalsTable from './components/PendingApprovalsTable'
import SharedDictionaryTable from './components/SharedDictionaryTable'
import { DICTIONARY_SNIPPETS_BASE_PATH, DICTIONARY_SNIPPETS_TAB_SLUGS, type DictionarySnippetsTabSlug } from './routes'

import styles from './DictionarySnippetsPage.module.scss'
import baseStyles from '@/pages/base.module.scss'

import Tabs from '@/components/Tabs'
import { PENDING_SHARED_QUERY, useSharedDictionary } from '@/hooks/useSharedDictionary'

// Keyed by slug, then ordered by `routes.ts`, so the slug list stays the single source of
// ordering and inserting a tab cannot silently re-slug its neighbours. `satisfies` makes a
// missing entry a compile error.
const TAB_BY_SLUG = {
  words: {
    label: 'Dictionary',
    Component: () => (
      <SharedDictionaryTable
        isSnippet={false}
        replacementHeader="Replaces with"
        emptyMessage="No dictionary words have been shared yet."
      />
    ),
  },
  snippets: {
    label: 'Snippets',
    Component: () => (
      <SharedDictionaryTable
        isSnippet
        replacementHeader="Expands to"
        emptyMessage="No snippets have been shared yet."
      />
    ),
  },
  pending: {
    label: 'Pending',
    Component: PendingApprovalsTable,
  },
} satisfies Record<DictionarySnippetsTabSlug, { label: string; Component: () => JSX.Element }>

const TABS = DICTIONARY_SNIPPETS_TAB_SLUGS.map((slug) => ({
  slug,
  ...TAB_BY_SLUG[slug],
}))

const DictionarySnippetsPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  // Page level: only the active tab mounts, so a count inside the queue would read zero.
  const pendingCount = useSharedDictionary(PENDING_SHARED_QUERY).data?.total ?? 0

  const activeTab = Math.max(
    0,
    TABS.findIndex((tab) => location.pathname.endsWith(`/${tab.slug}`))
  )
  const { Component: ActiveTabComponent } = TABS[activeTab]

  const handleTabChange = (index: number) => {
    navigate(`${DICTIONARY_SNIPPETS_BASE_PATH}/${TABS[index].slug}`)
  }

  // `Tabs` takes plain strings, so the count rides in the label (as TeamPage does). Hidden at 0.
  const tabLabels = TABS.map((tab) =>
    tab.slug === 'pending' && pendingCount > 0 ? `${tab.label} (${pendingCount})` : tab.label
  )

  return (
    <>
      <Helmet>
        <title>Wispr Flow | Dictionary & Snippets</title>
      </Helmet>
      <div className={clsx(baseStyles.container, styles.container)}>
        <div className={clsx(baseStyles.tabTitle, baseStyles.pageTabTitle)}>
          <h6>Dictionary &amp; Snippets</h6>
          <Tabs tabs={tabLabels} activeTab={activeTab} setActiveTab={handleTabChange} />
        </div>
        <ActiveTabComponent />
      </div>
    </>
  )
}

export default DictionarySnippetsPage
